/**
 * DevelopersSection — 01 Developers on the metered Usage page: each
 * developer by lane beside the same tokens at Claude list, their daily limit
 * (editable in place), the workspace total, the honesty lines, and the Spend
 * per day fold. A developer's row opens their own days in the sheet.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import type {
  YagniCodeDailyUsageResponse,
  YagniCodeDeveloperBudget,
  YagniCodeUsageDeveloperRow,
  YagniCodeUsageReport,
  YagniCodeWorkspaceDailyResponse,
} from "@yagni/shared";
import { YAGNI_CODE_LANES } from "@yagni/shared";

import { Btn, Fold, RecordHead } from "../queue/index.js";
import { sheetStore } from "../shell/useSheet.js";
import {
  cappableMembers,
  fetchDeveloperDaily,
  fillDailySeries,
  fillWorkspaceDailySeries,
  formatLimitCell,
  hasNoClaudePrice,
  laneLabel,
  parseUsd,
  shortDay,
  withCappedDevelopers,
  type Ledger,
  type WorkspaceMemberOption,
} from "./ledger.js";
import { rangeLabel, usd, type UsageRange } from "./usageReport.js";
import { Hist, Key, Legend, Note, Quiet, Sec, type HistColumn } from "./usageParts.js";

const NO_CLAUDE_TITLE = "This spend ran on a lane with no Claude list equivalent";
const PARTIAL_CLAUDE_TITLE =
  "Part of this spend ran on a lane with no Claude list equivalent and is left out of this figure";

const BILLED_COLOR = "var(--accent)";
const CLAUDE_COLOR = "rgba(var(--ink-rgb), .16)";

/** A Claude-list cell: a dash with its reason when nothing is priced, a mark when part is. */
function ClaudeFigure({ blank, partial = false, millicents }: { blank: boolean; partial?: boolean; millicents: number }) {
  if (blank) return <Quiet title={NO_CLAUDE_TITLE} />;
  if (partial) return <span title={PARTIAL_CLAUDE_TITLE}>{usd(millicents, { cents: true })}*</span>;
  return <>{usd(millicents, { cents: true })}</>;
}

function developerName(row: YagniCodeUsageDeveloperRow): string {
  return row.displayName ?? row.email ?? "Former member";
}

export function DevelopersSection({ range, ledger }: { range: UsageRange; ledger: Ledger }) {
  const { report, reportState, reportError, budgets, budgetError } = ledger;
  const [pending, setPending] = useState<WorkspaceMemberOption[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  const rows = useMemo(() => withCappedDevelopers(report?.developers ?? [], budgets, pending), [report, budgets, pending]);
  const addable = useMemo(() => (ledger.members ? cappableMembers(ledger.members, rows) : []), [ledger.members, rows]);
  const budgetByUser = useMemo(() => new Map((budgets ?? []).map((budget) => [budget.userId, budget])), [budgets]);

  // Every lane on the card is a column, and an unknown lane in the data gets one too.
  const lanes = useMemo(() => {
    const known = new Set<string>(YAGNI_CODE_LANES);
    const extras = report?.developers.flatMap((row) => row.lanes.map((lane) => lane.lane)).filter((lane) => !known.has(lane)) ?? [];
    return [...YAGNI_CODE_LANES, ...[...new Set(extras)].sort()];
  }, [report]);

  const beginEdit = (userId: string, current: YagniCodeDeveloperBudget | undefined) => {
    setEditing(userId);
    setDraftError(null);
    setDraft(current ? (current.dailyLimitMillicents / 100_000).toFixed(2) : "");
  };
  const commit = async (userId: string, next: number | null) => {
    if (await ledger.saveLimit(userId, next)) setEditing(null);
  };
  const submit = (userId: string) => {
    const parsed = parseUsd(draft);
    if (parsed === null) {
      setDraftError("Enter a dollar amount, for example 25.00.");
      return;
    }
    void commit(userId, parsed);
  };

  const openDays = (row: YagniCodeUsageDeveloperRow) => {
    if (row.userId === null) return;
    sheetStore.open(<DeveloperDaysSheet row={row} range={range} />);
  };

  const addPicker =
    addable.length > 0 && budgets !== null ? (
      <select
        aria-label="Set a limit for someone with no spend yet"
        className="btn quiet sm"
        style={{ border: 0, background: "transparent" }}
        value=""
        onChange={(event) => {
          const picked = addable.find((member) => member.userId === event.target.value);
          if (!picked) return;
          // Opens the row; nothing is written until an amount is saved.
          setPending((current) => [...current, picked]);
          beginEdit(picked.userId, undefined);
        }}
      >
        <option value="">Set a limit for someone with no spend yet</option>
        {addable.map((member) => (
          <option key={member.userId} value={member.userId}>
            {member.displayName ?? member.email ?? member.userId}
          </option>
        ))}
      </select>
    ) : undefined;

  const noWorkspaceClaude =
    report !== null && report.developers.length > 0 && report.developers.every((row) => hasNoClaudePrice(row.lanes));
  const laneTotals = (lane: string) =>
    (report?.developers ?? []).reduce(
      (sum, row) => sum + (row.lanes.find((cell) => cell.lane === lane)?.sellMillicents ?? 0),
      0,
    );

  return (
    <Sec
      k="01"
      title="Developers"
      fact="YAGNI Code, Claude Code and Codex through the proxy · lanes are the four priced levels on the rate card"
      right={addPicker}
      testId="usage-developers"
    >
      {reportState === "loading" && <Note>Loading usage</Note>}
      {reportState === "error" && <Note tone="honest">{reportError}</Note>}
      {reportState === "forbidden" && <Note>Per-developer usage is visible to workspace admins.</Note>}

      {reportState === "ready" && report && (
        <>
          {rows.length === 0 ? (
            <div className="empty dashed tight">
              <p>No YAGNI Code usage in this range.</p>
            </div>
          ) : (
            <div className="tbl" data-testid="developer-table">
              <table>
                <thead>
                  <tr>
                    <th>Developer</th>
                    {lanes.map((lane) => (
                      <th key={lane} className="num">
                        {laneLabel(lane)}
                      </th>
                    ))}
                    <th className="num">Dispatches</th>
                    <th className="num">Total</th>
                    <th className="num">At Claude list</th>
                    <th className="num">Saved</th>
                    <th>Daily limit</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const bucket = row.userId === null;
                    const userId = row.userId ?? "";
                    const blank = hasNoClaudePrice(row.lanes);
                    const budget = budgetByUser.get(userId);
                    return (
                      <tr
                        key={row.userId ?? "unattributed"}
                        data-drill={bucket ? undefined : "developer"}
                        onClick={bucket ? undefined : () => openDays(row)}
                      >
                        <td>
                          {bucket ? (
                            <span style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>Not attributed to a seat</span>
                          ) : (
                            <button
                              type="button"
                              className="usage-name"
                              style={{ textAlign: "left", cursor: "pointer" }}
                              aria-label={`${developerName(row)}: spend per day`}
                              onClick={(event) => {
                                event.stopPropagation();
                                openDays(row);
                              }}
                            >
                              {developerName(row)}
                              {row.email && row.displayName && <span className="sub">{row.email}</span>}
                            </button>
                          )}
                        </td>
                        {lanes.map((lane) => {
                          const cell = row.lanes.find((candidate) => candidate.lane === lane);
                          return (
                            <td key={lane} className="num">
                              {cell ? usd(cell.sellMillicents, { cents: true }) : <Quiet />}
                            </td>
                          );
                        })}
                        <td className="num">{row.totalDispatches.toLocaleString("en-US")}</td>
                        <td className="num" style={{ fontWeight: 600 }}>
                          {usd(row.totalSellMillicents, { cents: true })}
                        </td>
                        <td className="num">
                          <ClaudeFigure blank={blank} partial={row.counterfactualIncomplete} millicents={row.totalCounterfactualMillicents} />
                        </td>
                        <td className="num" style={{ color: "var(--accent-active)" }}>
                          <ClaudeFigure blank={blank} partial={row.counterfactualIncomplete} millicents={row.totalSavingsMillicents} />
                        </td>
                        <td onClick={(event) => event.stopPropagation()}>
                          {bucket ? (
                            <span className="fact" title="This spend has no initiating developer, so it cannot carry a limit.">
                              Not capped
                            </span>
                          ) : budgets === null ? (
                            <span
                              className="fact"
                              title={
                                budgetError
                                  ? "The current limit could not be loaded, so it cannot be edited safely."
                                  : "Loading the current limit"
                              }
                            >
                              {budgetError ? "Unavailable" : "…"}
                            </span>
                          ) : editing === userId ? (
                            <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                              <span className="field" style={{ display: "contents" }}>
                                <span className="in" style={{ width: 90, height: 28 }}>
                                  <input
                                    aria-label={`Daily limit for ${developerName(row)}`}
                                    value={draft}
                                    autoFocus
                                    onChange={(event) => setDraft(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter") submit(userId);
                                      if (event.key === "Escape") setEditing(null);
                                    }}
                                  />
                                </span>
                              </span>
                              <Btn variant="primary" size="sm" disabled={ledger.savingUserId === userId} onClick={() => submit(userId)}>
                                Save
                              </Btn>
                              {budget && (
                                <Btn variant="quiet" size="sm" disabled={ledger.savingUserId === userId} onClick={() => void commit(userId, null)}>
                                  Clear
                                </Btn>
                              )}
                            </span>
                          ) : (
                            <button
                              type="button"
                              style={{
                                font: "400 12px/1 var(--mono)",
                                cursor: "pointer",
                                color: budget?.overLimit ? "var(--honesty)" : "var(--text-secondary)",
                                textDecoration: "underline dotted",
                                textUnderlineOffset: 4,
                              }}
                              onClick={() => beginEdit(userId, budget)}
                            >
                              {formatLimitCell(budget)}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="total" data-testid="developer-total">
                    <td>Workspace total</td>
                    {lanes.map((lane) => (
                      <td key={lane} className="num">
                        {laneTotals(lane) > 0 ? usd(laneTotals(lane), { cents: true }) : <Quiet />}
                      </td>
                    ))}
                    <td className="num">{report.totalDispatches.toLocaleString("en-US")}</td>
                    <td className="num">{usd(report.totalSellMillicents, { cents: true })}</td>
                    <td className="num">
                      <ClaudeFigure blank={noWorkspaceClaude} partial={report.counterfactualIncomplete} millicents={report.totalCounterfactualMillicents} />
                    </td>
                    <td className="num" style={{ color: "var(--accent-active)" }}>
                      <ClaudeFigure blank={noWorkspaceClaude} partial={report.counterfactualIncomplete} millicents={report.totalSavingsMillicents} />
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
              <HonestyLines report={report} ledger={ledger} />
            </div>
          )}
          {(draftError || budgetError) && (
            <p className="fact" data-testid="budget-error" role="alert">
              {draftError ?? `${budgetError} Spend figures above are unaffected.`}
            </p>
          )}
          <Note>
            Spend is what your workspace pays at its contracted lane rates. At Claude list is the same tokens at the
            Claude model matched to each lane, at Anthropic's list price on the day, with Claude's prompt writing
            included. Token detail per developer and lane is in the CSV.
            {report.counterfactualIncomplete ? " A figure marked * covers only the lanes with a Claude list equivalent." : ""}
          </Note>
          <SpendPerDay daily={ledger.daily} error={ledger.dailyError} />
        </>
      )}
    </Sec>
  );
}

/** The amber lines under the table: limits not enforced, spend with no seat, spend never charged. */
function HonestyLines({ report, ledger }: { report: YagniCodeUsageReport; ledger: Ledger }) {
  const lines: string[] = [];
  if (!ledger.enforced && (ledger.budgets?.length ?? 0) > 0) {
    lines.push("Daily limits are saved but not being enforced yet.");
  }
  if (report.unattributedSellMillicents > 0) {
    lines.push(
      `${usd(report.unattributedSellMillicents, { cents: true })} is not attributed to a seat: autonomous runs and system sessions have no developer behind them, so it is its own row and the table still adds up.`,
    );
  }
  if (report.unbilledSellMillicents > 0) {
    lines.push(
      `${usd(report.unbilledSellMillicents, { cents: true })} of the spend shown was not charged, due to insufficient credits or a billing error. Usage is reported at your contracted rates either way.`,
    );
  }
  if (lines.length === 0) return null;
  return (
    <div className="honest" data-testid="developer-honesty">
      {lines.join(" ")}
    </div>
  );
}

function SpendPerDay({ daily, error }: { daily: YagniCodeWorkspaceDailyResponse | null; error: string | null }) {
  const [openDay, setOpenDay] = useState<string | null>(null);
  if (error !== null) return <Note>Spend per day could not be loaded. Figures above are unaffected.</Note>;
  if (daily === null || daily.days.length === 0) return null;
  const series = fillWorkspaceDailySeries(daily.from, daily.to, daily.days);
  const columns: HistColumn[] = series.map((day) => ({
    key: day.day,
    label: shortDay(day.day),
    value: day.totalSellMillicents > 0 ? usd(day.totalSellMillicents) : "",
    bars: [
      { amount: day.totalSellMillicents, color: BILLED_COLOR },
      { amount: day.totalCounterfactualMillicents, color: CLAUDE_COLOR },
    ],
    aria: `${day.day}: ${usd(day.totalSellMillicents, { cents: true })} across ${day.totalDispatches} dispatches`,
  }));
  return (
    <Fold title="Spend per day" count={`${series.length} days`}>
      <div data-testid="workspace-daily" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Hist columns={columns} label={`Workspace spend per day, ${daily.from} onward`} />
        <Legend>
          <Key className="k-bl">billed</Key>
          <Key color={CLAUDE_COLOR}>at Claude list</Key>
          <span style={{ marginLeft: "auto" }}>open a developer above for their own days</span>
        </Legend>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Day</th>
                <th className="num">Dispatches</th>
                <th className="num">Billed</th>
                <th className="num">At Claude list</th>
                <th className="num">Saved</th>
              </tr>
            </thead>
            <tbody>
              {daily.days.map((day) => {
                const open = openDay === day.day;
                const blank = hasNoClaudePrice(day.lanes);
                return (
                  <Fragment key={day.day}>
                    <tr data-drill="day" onClick={() => setOpenDay(open ? null : day.day)}>
                      <td>
                        <button type="button" aria-expanded={open} style={{ color: "var(--accent)", cursor: "pointer" }}>
                          {day.day}
                        </button>
                      </td>
                      <td className="num">{day.totalDispatches.toLocaleString("en-US")}</td>
                      <td className="num">{usd(day.totalSellMillicents, { cents: true })}</td>
                      <td className="num">
                        <ClaudeFigure blank={blank} partial={day.counterfactualIncomplete} millicents={day.totalCounterfactualMillicents} />
                      </td>
                      <td className="num" style={{ color: "var(--accent-active)" }}>
                        <ClaudeFigure blank={blank} partial={day.counterfactualIncomplete} millicents={day.totalSavingsMillicents} />
                      </td>
                    </tr>
                    {open &&
                      day.lanes.map((lane) => (
                        <tr key={`${day.day} ${lane.lane}`}>
                          <td style={{ paddingLeft: 28, color: "var(--text-secondary)" }}>{laneLabel(lane.lane)}</td>
                          <td className="num">{lane.dispatches.toLocaleString("en-US")}</td>
                          <td className="num">{usd(lane.sellMillicents, { cents: true })}</td>
                          <td className="num">
                            <ClaudeFigure blank={lane.counterfactualMillicents === null} millicents={lane.counterfactualMillicents ?? 0} />
                          </td>
                          <td className="num">
                            <ClaudeFigure blank={lane.savingsMillicents === null} millicents={lane.savingsMillicents ?? 0} />
                          </td>
                        </tr>
                      ))}
                    {open && day.unbilledSellMillicents > 0 && (
                      <tr>
                        <td colSpan={5} className="fact" style={{ paddingLeft: 28 }}>
                          {usd(day.unbilledSellMillicents, { cents: true })} of this day was not charged, due to
                          insufficient credits or a billing error.
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Note>
          Includes usage without a developer seat, so days add up to the table above. Days are UTC; the table omits
          days with no usage.
        </Note>
      </div>
    </Fold>
  );
}

/** One developer's days, in the sheet: the bars, the range's totals, and the day table by lane. */
export function DeveloperDaysSheet({ row, range }: { row: YagniCodeUsageDeveloperRow; range: UsageRange }) {
  const [data, setData] = useState<YagniCodeDailyUsageResponse | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const userId = row.userId ?? "";
  useEffect(() => {
    let cancelled = false;
    fetchDeveloperDaily(userId, range)
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch((err: unknown) => {
        if (!cancelled) setFailed(err instanceof Error ? err.message : "Failed to load daily usage");
      });
    return () => {
      cancelled = true;
    };
  }, [userId, range]);

  const name = developerName(row);
  const series = data ? fillDailySeries(data.from, data.to, data.days) : [];
  const totalSell = series.reduce((sum, point) => sum + point.sellMillicents, 0);
  const totalClaude = series.reduce((sum, point) => sum + point.counterfactualMillicents, 0);
  const incomplete = series.some((point) => point.counterfactualIncomplete);

  return (
    <>
      <RecordHead
        recordKey="Developer"
        title={`${name}: spend per day`}
        meta={<span className="fact">{rangeLabel(range)}</span>}
        noExpand
      />
      <div className="sbody" data-testid="daily-drilldown">
        {failed ? (
          <Note>Daily detail could not be loaded ({failed}). Spend figures on the page are unaffected.</Note>
        ) : !data ? (
          <Note>Loading daily detail for {name}</Note>
        ) : (
          <>
            <Hist
              label={`Daily spend for ${name}, ${data.from} onward`}
              columns={series.map((point) => ({
                key: point.day,
                label: shortDay(point.day),
                value: point.sellMillicents > 0 ? usd(point.sellMillicents) : "",
                bars: [
                  { amount: point.sellMillicents, color: BILLED_COLOR },
                  { amount: point.counterfactualMillicents, color: CLAUDE_COLOR },
                ],
                aria: `${point.day}: ${usd(point.sellMillicents, { cents: true })} across ${point.dispatches} dispatches`,
              }))}
            />
            <p className="fact">
              This range: {usd(totalSell, { cents: true })} at contracted rates; {usd(totalClaude, { cents: true })} at
              Claude list prices
              {incomplete ? " (some usage had no Claude list equivalent and is excluded)" : ""}. Days are UTC.
            </p>
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>Lane</th>
                    <th className="num">Dispatches</th>
                    <th className="num">Billed</th>
                  </tr>
                </thead>
                <tbody>
                  {row.lanes.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="fact">
                        No spend in this range.
                      </td>
                    </tr>
                  ) : (
                    row.lanes.map((lane) => (
                      <tr key={lane.lane}>
                        <td>{laneLabel(lane.lane)}</td>
                        <td className="num">{lane.dispatches.toLocaleString("en-US")}</td>
                        <td className="num">{usd(lane.sellMillicents, { cents: true })}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}
